import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, TextInput, TextStyle, View } from 'react-native';

import { IconSearchSvg } from '../../../assets/friends';
import { friendsSearchPlaceholder } from './friendsCatalog';
import { FRIENDS_FONT, friendsColors, hairline, inset, RADIUS_MD } from './friendsTheme';

/**
 * Figma "Search row" (4244:41093) - the field that narrows the list, with the player's friend
 * count pinned to its right. The design draws the field with its placeholder showing; here it is
 * a real input, because a search box that cannot be typed into is the one thing the row is for.
 *
 * The counter is the player's total, not the number of rows on screen, so typing does not move it.
 * The design's "12 / 50" has a cap the friends service does not have, so only the count shows.
 */
// The row is 334 wide; its 42 height is the field's 11 of padding around a 20-tall line.
const SEARCH_DESIGN_WIDTH = 334;

const ROW_GAP = 12;
const FIELD = { paddingX: 12, paddingY: 11, gap: 8, icon: 20, text: 14 };
const COUNTER = { gap: 2, label: 12, value: 16 };

/** `outlineStyle` is React Native Web's; the cast is how the rest of the app spells it. */
type WebInputStyle = TextStyle & { outlineStyle?: 'none' };

type Props = {
	value: string;
	onChangeText: (value: string) => void;
	/** Null until the first unfiltered load. */
	friendsCount: number | null;
	scale: number;
};

export const FriendsSearch = ({ value, onChangeText, friendsCount, scale }: Props) => {
	const styles = useMemo(() => createStyles(scale), [scale]);
	const { t } = useTranslation();

	return (
		<View style={styles.row}>
			<View style={styles.field}>
				{/* Wrapped so the SVGR icon keeps its place in the paint order on web. */}
				<View>
					<IconSearchSvg width={FIELD.icon * scale} height={FIELD.icon * scale} />
				</View>
				<TextInput
					value={value}
					onChangeText={onChangeText}
					placeholder={friendsSearchPlaceholder()}
					placeholderTextColor={friendsColors.muted}
					accessibilityLabel={t('T_SITE_A11Y_SEARCH_FRIENDS', {
						defaultValue: 'Search friends by username',
					})}
					autoCapitalize='none'
					autoCorrect={false}
					style={styles.input}
				/>
			</View>

			<View style={styles.counter}>
				<Text numberOfLines={1} style={styles.counterLabel}>
					{t('T_SITE_MENU_FRIENDS', { defaultValue: 'Friends' })}
				</Text>
				<Text numberOfLines={1} style={styles.counterValue}>
					{friendsCount ?? '-'}
				</Text>
			</View>
		</View>
	);
};

const createStyles = (scale: number) =>
	StyleSheet.create({
		row: {
			width: SEARCH_DESIGN_WIDTH * scale,
			flexDirection: 'row',
			alignItems: 'center',
			gap: ROW_GAP * scale,
		},
		field: {
			flex: 1,
			flexDirection: 'row',
			alignItems: 'center',
			gap: FIELD.gap * scale,
			paddingHorizontal: inset(FIELD.paddingX, scale),
			paddingVertical: inset(FIELD.paddingY, scale),
			borderRadius: RADIUS_MD * scale,
			borderWidth: hairline(scale),
			borderColor: friendsColors.border,
			backgroundColor: friendsColors.surface,
		},
		// Figma "DS/Body/M": Sofia Sans Medium 14, line-height 1.4. The vertical padding lives on the
		// field, so the input contributes exactly its 20-tall line and the row stays 42.
		input: {
			flex: 1,
			height: FIELD.icon * scale,
			padding: 0,
			color: friendsColors.gold50,
			fontFamily: FRIENDS_FONT,
			fontSize: FIELD.text * scale,
			fontWeight: '500',
			outlineStyle: 'none',
		} as WebInputStyle,
		counter: {
			alignItems: 'flex-end',
			gap: COUNTER.gap * scale,
		},
		// Figma "DS/Body/S": Sofia Sans Medium 12, line-height 1.4.
		counterLabel: {
			color: friendsColors.muted,
			fontFamily: FRIENDS_FONT,
			fontSize: COUNTER.label * scale,
			fontWeight: '500',
			lineHeight: COUNTER.label * 1.4 * scale,
		},
		// Figma "DS/Numeric/M": Sofia Sans Bold 16, line-height 1, letter-spacing -1%.
		counterValue: {
			color: friendsColors.accent,
			fontFamily: FRIENDS_FONT,
			fontSize: COUNTER.value * scale,
			fontWeight: '700',
			letterSpacing: -0.16 * scale,
			lineHeight: COUNTER.value * scale,
		},
	});

export default FriendsSearch;
